import { useEffect, useState } from "react";
import { Cookie } from "lucide-react";

const KEY = "croclab-cookie-consent";

const readConsent = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY));
  } catch {
    return null;
  }
};

const applyConsent = (consent) => {
  if (window.posthog && window.posthog.opt_in_capturing) {
    if (consent.analytics) {
      window.posthog.opt_in_capturing();
    } else {
      window.posthog.opt_out_capturing();
    }
  }
};

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [configuring, setConfiguring] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    const stored = readConsent();
    if (stored) {
      applyConsent(stored);
    } else {
      setVisible(true);
    }
    const reopen = () => {
      const current = readConsent();
      setAnalytics(current?.analytics ?? false);
      setConfiguring(true);
      setVisible(true);
    };
    window.addEventListener("croclab-open-cookies", reopen);
    return () => window.removeEventListener("croclab-open-cookies", reopen);
  }, []);

  const save = (value) => {
    const consent = { analytics: value, date: new Date().toISOString() };
    localStorage.setItem(KEY, JSON.stringify(consent));
    applyConsent(consent);
    setVisible(false);
    setConfiguring(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-label="Consentimiento de cookies"
      className="fixed bottom-4 inset-x-4 z-50"
    >
      <div className="max-w-3xl mx-auto bg-white rounded-3xl border border-brand-charcoal/10 shadow-xl p-6 md:p-8">
        <div className="flex items-start gap-4">
          <span className="w-11 h-11 rounded-full bg-brand-pink/10 text-brand-pink flex items-center justify-center shrink-0">
            <Cookie className="w-5 h-5" aria-hidden="true" />
          </span>
          <div>
            <p className="font-display font-semibold text-lg text-brand-charcoal">
              Cookies, pero de las digitales
            </p>
            <p className="mt-2 text-sm text-brand-charcoal/70 leading-relaxed">
              Utilizamos almacenamiento técnico imprescindible y, solo si tú lo
              aceptas, cookies de analítica para entender cómo se usa la web. No
              instalamos ninguna cookie no esencial antes de tu consentimiento.
              Más información en nuestra{" "}
              <a
                href="/cookies"
                data-testid="cookie-policy-link"
                className="text-brand-pink font-semibold hover:underline"
              >
                Política de Cookies
              </a>
              .
            </p>
          </div>
        </div>

        {configuring && (
          <div
            data-testid="cookie-settings-panel"
            className="mt-5 border-t border-brand-charcoal/10 pt-5 space-y-4"
          >
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-brand-charcoal">Técnicas</p>
                <p className="text-xs text-brand-charcoal/55">
                  Necesarias para recordar tu elección. Siempre activas.
                </p>
              </div>
              <span className="text-xs font-semibold text-brand-charcoal/45 uppercase tracking-wide">
                Siempre activas
              </span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-brand-charcoal">Analítica</p>
                <p className="text-xs text-brand-charcoal/55">
                  PostHog: estadísticas de uso anónimas de la web.
                </p>
              </div>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="cookie-analytics-toggle"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                  className="w-5 h-5 accent-[#FF72AE]"
                />
                <span className="sr-only">Activar o desactivar cookies de analítica</span>
              </label>
            </div>
          </div>
        )}

        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          {configuring ? (
            <button
              data-testid="cookie-save-button"
              onClick={() => save(analytics)}
              className="flex-1 inline-flex items-center justify-center bg-brand-pink text-white font-semibold rounded-full px-6 py-3 hover:bg-brand-pink/90 transition-colors"
            >
              Guardar preferencias
            </button>
          ) : (
            <>
              <button
                data-testid="cookie-accept-button"
                onClick={() => save(true)}
                className="flex-1 inline-flex items-center justify-center bg-brand-pink text-white font-semibold rounded-full px-6 py-3 hover:bg-brand-pink/90 transition-colors"
              >
                Aceptar todas
              </button>
              <button
                data-testid="cookie-reject-button"
                onClick={() => save(false)}
                className="flex-1 inline-flex items-center justify-center border-2 border-brand-charcoal text-brand-charcoal font-semibold rounded-full px-6 py-3 hover:bg-brand-charcoal hover:text-white transition-colors"
              >
                Rechazar no esenciales
              </button>
              <button
                data-testid="cookie-configure-button"
                onClick={() => setConfiguring(true)}
                className="flex-1 inline-flex items-center justify-center text-sm font-semibold text-brand-charcoal/70 hover:text-brand-pink transition-colors rounded-full px-6 py-3"
              >
                Configurar
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
